import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { fmtHM } from '../lib/format.js';
import { ExcludedBadge } from './ExcludedBadge.jsx';
import { Icon } from './Icon.jsx';
import { Select } from './Select.jsx';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Per-card palettes — mirror the segmented-meter approach on Today:
// each card sticks to one hue family but mixes bright/saturated and
// pastel tones so adjacent rows read as distinct rather than a smooth
// fade. Activity → mint (matches Today's progress meter); Client →
// blue; Jira ticket → violet.
const PALETTE_BY_CARD = {
  activity: ['#05E6C5', '#79F1E0', '#00C1C1', '#B2F7ED', '#58EED8'],
  client: ['#2E6BE6', '#8FB4FF', '#1F4FB0', '#C3D6FF', '#5D8EF5'],
  ticket: ['#7B5BE0', '#C4B0F5', '#5A3FB8', '#E2D6FF', '#9E80EE'],
};

// FNV-1a 32-bit, simple deterministic string hash. Used to scatter row
// colors by row key — same key always picks the same shade across
// renders, but neighbors never collapse onto a monotonic gradient.
function hashStr(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function shadeForRow(palette, key, idx) {
  // +idx so even when two keys hash to the same modulo bucket they
  // still end up on different shades within the family.
  return palette[(hashStr(String(key)) + idx) % palette.length];
}

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function parseMonthKey(k) {
  const [y, m] = k.split('-').map(Number);
  return new Date(y, m - 1, 1);
}

function monthBounds(year, month /* 0-11 */) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`;
  return { from: fmt(first), to: fmt(last) };
}

// Mon–Fri minus the user's country holidays. v2.0 (A3): public holidays
// no longer count toward the monthly target. `holidayByDate` is a
// Map<isoDate, holiday>; pass an empty Map (or null) to fall back to the
// pre-v2.0 weekday-only math.
function isHolidayDate(year, month, day, holidayByDate) {
  if (!holidayByDate || !holidayByDate.size) return false;
  const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(
    day,
  ).padStart(2, '0')}`;
  return holidayByDate.has(iso);
}

function workingDaysInMonth(year, month, holidayByDate) {
  const last = new Date(year, month + 1, 0).getDate();
  let count = 0;
  for (let d = 1; d <= last; d++) {
    const dow = new Date(year, month, d).getDay();
    if (dow === 0 || dow === 6) continue;
    if (isHolidayDate(year, month, d, holidayByDate)) continue;
    count += 1;
  }
  return count;
}

function workingDaysElapsed(year, month, today, holidayByDate) {
  if (
    year > today.getFullYear() ||
    (year === today.getFullYear() && month > today.getMonth())
  ) {
    return 0;
  }
  if (
    year < today.getFullYear() ||
    (year === today.getFullYear() && month < today.getMonth())
  ) {
    return workingDaysInMonth(year, month, holidayByDate);
  }
  let count = 0;
  for (let d = 1; d <= today.getDate(); d++) {
    const dow = new Date(year, month, d).getDay();
    if (dow === 0 || dow === 6) continue;
    if (isHolidayDate(year, month, d, holidayByDate)) continue;
    count += 1;
  }
  return count;
}

// v2.0 follow-up — count working days within (year, month) that ALSO
// fall inside [fromIso, toIso]. Mirrors the server-side helper so
// hero math agrees with /api/team math when the user joined or left
// mid-month.
function isoOf(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
function workingDaysInRange(year, month, holidayByDate, fromIso, toIso) {
  if (!fromIso || !toIso || fromIso > toIso) return 0;
  const last = new Date(year, month + 1, 0).getDate();
  let count = 0;
  for (let d = 1; d <= last; d++) {
    const iso = isoOf(year, month, d);
    if (iso < fromIso || iso > toIso) continue;
    const dow = new Date(year, month, d).getDay();
    if (dow === 0 || dow === 6) continue;
    if (isHolidayDate(year, month, d, holidayByDate)) continue;
    count += 1;
  }
  return count;
}

// v2.0 — hard floor on the month picker. ClockIn went live in May 2026;
// earlier months have no data and would only confuse users. Once the
// rolling 12-month window naturally clears May 2026 (i.e. after May
// 2027), this floor stops mattering.
const MONTH_HARD_FLOOR = '2026-05';

function buildMonthOptions(today) {
  const opts = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const key = monthKey(d);
    if (key < MONTH_HARD_FLOOR) break; // months earlier than May 2026 are hidden
    opts.push({
      key,
      label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
    });
  }
  return opts;
}

function RatioBar({ value, max, color }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div
      style={{
        height: 6,
        borderRadius: 999,
        background: 'var(--sp-bg-muted)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: color,
          borderRadius: 999,
          transition: 'width .3s ease',
        }}
      />
    </div>
  );
}

function BreakdownCard({ title, icon, rows, emptyText, totalMins, palette }) {
  return (
    <div
      className="card"
      style={{
        padding: '18px 20px 16px',
        borderRadius: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: 'var(--sp-fg-3)', display: 'inline-flex' }}>
          <Icon name={icon} size={14} />
        </span>
        <h3
          style={{
            margin: 0,
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: '-0.005em',
            color: 'var(--sp-fg-1)',
          }}
        >
          {title}
        </h3>
      </div>
      {rows.length === 0 ? (
        <div
          style={{
            padding: '24px 0 12px',
            fontSize: 13,
            color: 'var(--sp-fg-3)',
            textAlign: 'center',
          }}
        >
          {emptyText}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map((r, i) => {
            const color = r.muted
              ? 'var(--sp-fg-4)'
              : shadeForRow(palette, r.key, i);
            return (
              <div
                key={r.key}
                style={{ display: 'flex', flexDirection: 'column', gap: 5 }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 13,
                      fontWeight: r.muted ? 500 : 600,
                      color: r.muted ? 'var(--sp-fg-3)' : 'var(--sp-fg-1)',
                      fontStyle: r.muted ? 'italic' : 'normal',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={r.label}
                  >
                    {r.prefix && (
                      <span
                        style={{
                          fontFamily: 'DM Sans',
                          color: 'var(--sp-fg-2)',
                          fontWeight: 700,
                          marginRight: 6,
                        }}
                      >
                        {r.prefix}
                      </span>
                    )}
                    {r.label}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--sp-fg-2)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {fmtHM(r.mins)}
                  </span>
                  <span
                    style={{
                      width: 44,
                      textAlign: 'right',
                      fontSize: 11,
                      color: 'var(--sp-fg-3)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {totalMins ? Math.round((r.mins / totalMins) * 100) : 0}%
                  </span>
                </div>
                <RatioBar value={r.mins} max={rows[0].mins} color={color} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--sp-fg-3)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: '-0.01em',
          color: 'var(--sp-fg-1)',
          fontVariantNumeric: 'tabular-nums',
          marginTop: 2,
        }}
      >
        {value}
      </div>
      {hint && (
        <div
          style={{ fontSize: 11, color: 'var(--sp-fg-3)', marginTop: 1 }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

/**
 * Monthly time summary — hero card showing reported vs expected hours for
 * the picked month (current month compares against month-to-date, past
 * months against full target), plus three stacked breakdowns: by
 * activity, by client, by Jira ticket. Entries without a client or
 * ticket fall into muted "Internal (no client)" / "No Jira ticket"
 * buckets so the user can see how much falls through.
 */
export function SummaryView({
  dailyHoursTarget = 8,
  holidays = { country: '', byDate: new Map() },
  // v2.0 (A4) — approver drill-in: when provided, switch to fetching
  // entries via the impersonation endpoint and render a read-only
  // banner explaining whose data the approver is looking at.
  subjectEmail = null,
  subjectName = null,
  // v2.0 follow-up — the caller's own active-range bounds. The hero
  // expected hours are prorated when these clip the selected month
  // (e.g. someone who started 2026-06-15 doesn't owe the whole of
  // June). Empty strings = unbounded. Ignored when drilled in,
  // because the server already prorated the report's row.
  userEnabledOn = '',
  userDisabledOn = '',
  // v2.6 — excluded user ("not required to log time"). Drops the deficit
  // framing: the status chip reads "Logging optional", the progress bar
  // goes neutral mint, the caption becomes "logged this month", and an
  // informational banner explains the figures don't count toward targets.
  // True for the excluded user's own Summary and for an approver drilling
  // into one (alongside the read-only banner + an Excluded badge).
  exempt = false,
}) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const dailyTargetMin = dailyHoursTarget * 60;
  const holidayByDate = holidays?.byDate || new Map();
  const monthOptions = useMemo(() => buildMonthOptions(today), [today]);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].key);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // v2.0 — admin-configurable override pulled from `monthly_targets`.
  // `targetHours === null` means "no override, use computed default."
  // We fetch on every (month, country) change rather than caching
  // app-wide; the endpoint is cheap and the override may flip after
  // ops edits the sheet.
  const [targetOverride, setTargetOverride] = useState(null);

  const { year, month } = useMemo(() => {
    const d = parseMonthKey(selectedMonth);
    return { year: d.getFullYear(), month: d.getMonth() };
  }, [selectedMonth]);

  useEffect(() => {
    let cancelled = false;
    const { from, to } = monthBounds(year, month);
    setLoading(true);
    setError(null);
    const fetcher = subjectEmail
      ? api.entriesRangeFor(subjectEmail, from, to)
      : api.entriesRange(from, to);
    fetcher
      .then((data) => {
        if (cancelled) return;
        setEntries(data.entries || []);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Could not load summary for this month.');
        setEntries([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, month, subjectEmail]);

  // v2.0 — fetch the `monthly_targets` override for (selectedMonth,
  // user's country). Null = no override, fall through to computed.
  useEffect(() => {
    let cancelled = false;
    api
      .monthlyTarget(selectedMonth, holidays?.country || undefined)
      .then((data) => {
        if (!cancelled) setTargetOverride(data.targetHours ?? null);
      })
      .catch(() => {
        if (!cancelled) setTargetOverride(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMonth, holidays?.country]);

  const reportedMins = useMemo(
    () =>
      entries.reduce((s, e) => s + (e.hours || 0) * 60 + (e.minutes || 0), 0),
    [entries],
  );
  // Full-month working days for the selected country — drives the
  // monthly_targets daily-rate derivation when an override is set.
  const fullMonthWorkingDays = useMemo(
    () => workingDaysInMonth(year, month, holidayByDate),
    [year, month, holidayByDate],
  );
  const fullMonthElapsedWorkingDays = useMemo(
    () => workingDaysElapsed(year, month, today, holidayByDate),
    [year, month, today, holidayByDate],
  );

  // v2.0 follow-up — clamp to the user's active range. In drill-in
  // mode (subjectEmail set) we don't have the report's enabled_on /
  // disabled_on locally and fall back to the full-month numbers; the
  // server-side aggregate row in /api/team is the source of truth in
  // that case.
  const { totalWorkingDays, elapsedWorkingDays } = useMemo(() => {
    if (subjectEmail) {
      return {
        totalWorkingDays: fullMonthWorkingDays,
        elapsedWorkingDays: fullMonthElapsedWorkingDays,
      };
    }
    const monthFirst = isoOf(year, month, 1);
    const monthLast = isoOf(year, month, new Date(year, month + 1, 0).getDate());
    const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const lowerBound =
      userEnabledOn && userEnabledOn > monthFirst ? userEnabledOn : monthFirst;
    const upperBound =
      userDisabledOn && userDisabledOn < monthLast ? userDisabledOn : monthLast;
    // No active overlap with this month → both stats are zero.
    if (lowerBound > upperBound) {
      return { totalWorkingDays: 0, elapsedWorkingDays: 0 };
    }
    const total = workingDaysInRange(
      year,
      month,
      holidayByDate,
      lowerBound,
      upperBound,
    );
    const elapsed = workingDaysInRange(
      year,
      month,
      holidayByDate,
      lowerBound,
      upperBound < todayIso ? upperBound : todayIso,
    );
    return { totalWorkingDays: total, elapsedWorkingDays: elapsed };
  }, [
    year,
    month,
    today,
    holidayByDate,
    subjectEmail,
    userEnabledOn,
    userDisabledOn,
    fullMonthWorkingDays,
    fullMonthElapsedWorkingDays,
  ]);
  const isCurrentMonth =
    year === today.getFullYear() && month === today.getMonth();
  const isFutureMonth =
    year > today.getFullYear() ||
    (year === today.getFullYear() && month > today.getMonth());

  // v2.0 follow-up — derive the daily rate from the monthly_targets
  // override when one's set, then prorate by the user's working days
  // in their active range within this month. Without an override the
  // rate is just DAILY_HOURS_TARGET. The pattern matches the server-
  // side /api/team prorating so Summary hero == Team-view row.
  const dailyRateMins =
    targetOverride && targetOverride > 0 && fullMonthWorkingDays > 0
      ? (targetOverride * 60) / fullMonthWorkingDays
      : dailyTargetMin;
  const expectedToDateMins = Math.round(elapsedWorkingDays * dailyRateMins);
  const expectedTotalMins = Math.round(totalWorkingDays * dailyRateMins);
  const expectedMins = isCurrentMonth ? expectedToDateMins : expectedTotalMins;
  const diffMins = reportedMins - expectedMins;
  const onTrack = diffMins >= 0;
  const pct =
    expectedMins > 0
      ? Math.min(120, Math.round((reportedMins / expectedMins) * 100))
      : 0;

  const byActivity = useMemo(() => {
    const m = new Map();
    entries.forEach((e) => {
      const mins = (e.hours || 0) * 60 + (e.minutes || 0);
      const key = e.activityName || '—';
      m.set(key, (m.get(key) || 0) + mins);
    });
    return Array.from(m, ([label, mins]) => ({ key: label, label, mins })).sort(
      (a, b) => b.mins - a.mins,
    );
  }, [entries]);

  const byClient = useMemo(() => {
    const m = new Map();
    entries.forEach((e) => {
      const mins = (e.hours || 0) * 60 + (e.minutes || 0);
      if (e.clientName) {
        const ex = m.get(e.clientName);
        if (ex) ex.mins += mins;
        else
          m.set(e.clientName, {
            key: e.clientName,
            label: e.clientName,
            mins,
          });
      } else {
        const k = '__none';
        const ex = m.get(k);
        if (ex) ex.mins += mins;
        else
          m.set(k, {
            key: k,
            label: 'Internal (no client)',
            mins,
            muted: true,
          });
      }
    });
    return Array.from(m.values()).sort((a, b) => b.mins - a.mins);
  }, [entries]);

  const byTicket = useMemo(() => {
    const m = new Map();
    entries.forEach((e) => {
      const mins = (e.hours || 0) * 60 + (e.minutes || 0);
      if (e.epicId) {
        const ex = m.get(e.epicId);
        if (ex) ex.mins += mins;
        else
          m.set(e.epicId, {
            key: e.epicId,
            prefix: e.epicId,
            label: e.epicTitle || '—',
            mins,
          });
      } else {
        const k = '__none';
        const ex = m.get(k);
        if (ex) ex.mins += mins;
        else
          m.set(k, {
            key: k,
            label: 'No Jira ticket',
            mins,
            muted: true,
          });
      }
    });
    return Array.from(m.values()).sort((a, b) => b.mins - a.mins);
  }, [entries]);

  const selectedLabel =
    monthOptions.find((o) => o.key === selectedMonth)?.label || '';

  return (
    <div
      data-screen-label="Summary"
      style={{
        flex: 1,
        overflowY: 'auto',
        background: 'var(--sp-bg-page)',
      }}
    >
      {/* v2.0 (A4 drill-in) — same apricot read-only pill as Today. Its
          own centered max-width column with the page gutter so it lines
          up with the content below; sits above the month heading. */}
      {subjectEmail && (
        <div
          style={{
            padding: '16px 28px 0',
            maxWidth: 1100,
            margin: '0 auto',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              background: '#FFF7E6',
              color: '#A05A00',
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="lock" size={12} /> Read-only — viewing{' '}
            {subjectName || subjectEmail}'s month
          </div>
          {/* v2.6 — mark a drilled-into excluded report. */}
          {exempt && <ExcludedBadge size="sm" />}
        </div>
      )}
      {/* v2.6 — informational banner on the excluded user's OWN Summary. */}
      {exempt && !subjectEmail && (
        <div
          style={{
            padding: '16px 28px 0',
            maxWidth: 1100,
            margin: '0 auto',
            width: '100%',
          }}
        >
          <div
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              background: 'var(--sp-mint-100)',
              color: '#007C7C',
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="clock" size={12} /> Not required to log time — these
            figures are informational and don't count toward targets or
            notifications.
          </div>
        </div>
      )}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 28px 48px' }}>
        {/* Header — month picker */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 20,
            marginBottom: 24,
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--sp-fg-3)',
                marginBottom: 4,
              }}
            >
              Time summary
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: 28,
                fontWeight: 700,
                letterSpacing: '-0.015em',
                color: 'var(--sp-fg-1)',
              }}
            >
              {selectedLabel}
            </h1>
          </div>
          <div style={{ width: 220 }}>
            <label className="tt-field-label" style={{ fontSize: 11 }}>
              Month
            </label>
            <Select
              value={selectedMonth}
              options={monthOptions}
              getKey={(o) => o.key}
              getLabel={(o) => o.label}
              onChange={(v) => setSelectedMonth(v)}
            />
          </div>
        </div>

        {error && (
          <div className="tt-error-msg" style={{ marginBottom: 16 }}>
            <Icon name="alert" size={12} /> {error}
          </div>
        )}

        {/* Hero — reported vs expected */}
        <div
          className="card"
          style={{
            padding: '24px 28px 24px',
            borderRadius: 18,
            marginBottom: 18,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.6fr 1fr',
              gap: 28,
              alignItems: 'center',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--sp-fg-3)',
                  marginBottom: 6,
                }}
              >
                {isFutureMonth
                  ? 'Expected (this month)'
                  : isCurrentMonth
                  ? 'Reported · month-to-date'
                  : 'Reported'}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                {loading ? (
                  <div className="tt-skel" style={{ width: 180, height: 44 }} />
                ) : (
                  <span
                    style={{
                      fontSize: 44,
                      fontWeight: 700,
                      letterSpacing: '-0.02em',
                      color: 'var(--sp-fg-1)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {fmtHM(reportedMins)}
                  </span>
                )}
                <span
                  style={{ fontSize: 16, color: 'var(--sp-fg-3)', fontWeight: 600 }}
                >
                  of {fmtHM(expectedMins)} expected
                </span>
              </div>

              <div
                style={{
                  marginTop: 14,
                  height: 10,
                  borderRadius: 999,
                  background: 'var(--sp-bg-muted)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, pct)}%`,
                    height: '100%',
                    // v2.6 — neutral mint for excluded users (no
                    // green=good / red=behind judgement).
                    background: exempt
                      ? 'linear-gradient(90deg, #58EED8, #00C1C1)'
                      : onTrack
                      ? 'linear-gradient(90deg, #1F8A5B, #2BB371)'
                      : 'linear-gradient(90deg, #C4320A, #E26847)',
                    borderRadius: 999,
                    transition: 'width .4s ease',
                  }}
                />
              </div>

              <div
                style={{
                  marginTop: 12,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: 'var(--sp-fg-2)',
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '4px 10px',
                    borderRadius: 999,
                    // v2.6 — neutral mint "Logging optional" chip for
                    // excluded users instead of ahead/short framing.
                    background: exempt
                      ? 'var(--sp-mint-100)'
                      : onTrack
                      ? 'rgba(31,138,91,0.12)'
                      : 'rgba(196,50,10,0.10)',
                    color: exempt ? '#007C7C' : onTrack ? '#1F8A5B' : '#C4320A',
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  <Icon
                    name={exempt ? 'clock' : onTrack ? 'check' : 'alert'}
                    size={12}
                    stroke={2.4}
                  />
                  {exempt
                    ? 'Logging optional'
                    : expectedMins === 0
                    ? 'Not started yet'
                    : onTrack
                    ? diffMins === 0
                      ? 'Right on target'
                      : `${fmtHM(diffMins)} ahead`
                    : `${fmtHM(-diffMins)} short`}
                </span>
                <span style={{ color: 'var(--sp-fg-3)' }}>
                  {exempt
                    ? 'logged this month'
                    : `${pct}% of ${
                        isCurrentMonth ? 'month-to-date target' : 'monthly target'
                      }`}
                </span>
              </div>
            </div>

            <div
              style={{
                borderLeft: '1px solid var(--sp-border)',
                paddingLeft: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              <Stat
                label="Working days"
                value={
                  isCurrentMonth
                    ? `${elapsedWorkingDays} / ${totalWorkingDays}`
                    : `${totalWorkingDays}`
                }
                hint={
                  // v2.0 (A3) — surface that public holidays are excluded
                  // when the user has a country with holidays loaded. Keep
                  // the existing copy when we don't know the country (e.g.
                  // the holidays endpoint failed silently).
                  holidays.country
                    ? `${isCurrentMonth ? 'elapsed / in month' : 'in month'} · excludes ${holidays.country} holidays`
                    : isCurrentMonth
                      ? 'elapsed / in month'
                      : 'in month'
                }
              />
              <Stat
                label="Daily target"
                value={fmtHM(dailyTargetMin)}
                hint="per working day"
              />
              <Stat
                label="Entries"
                value={entries.length}
                hint="logged in month"
              />
              {!isFutureMonth && expectedMins > 0 && (
                <Stat
                  label="Avg / day"
                  value={
                    elapsedWorkingDays > 0
                      ? fmtHM(Math.round(reportedMins / elapsedWorkingDays))
                      : '—'
                  }
                  hint={`across ${
                    isCurrentMonth ? 'elapsed' : 'all'
                  } working days`}
                />
              )}
            </div>
          </div>
        </div>

        {/* Breakdowns — stacked full-width for readability */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <BreakdownCard
            title="By activity"
            icon="flow"
            palette={PALETTE_BY_CARD.activity}
            rows={byActivity}
            totalMins={reportedMins}
            emptyText={loading ? 'Loading…' : 'No entries this month.'}
          />
          <BreakdownCard
            title="By client"
            icon="user"
            palette={PALETTE_BY_CARD.client}
            rows={byClient}
            totalMins={reportedMins}
            emptyText={loading ? 'Loading…' : 'No entries this month.'}
          />
          <BreakdownCard
            title="By Jira ticket"
            icon="jiraMark"
            palette={PALETTE_BY_CARD.ticket}
            rows={byTicket}
            totalMins={reportedMins}
            emptyText={loading ? 'Loading…' : 'No entries this month.'}
          />
        </div>
      </div>
    </div>
  );
}
